/**
 * Chute libre — la seconde expérience (SPEC §9).
 *
 * Même structure qu'Ératosthène, parce que c'est la structure du projet : deux
 * modèles prédisent la **même** grandeur observable, et ce qui les sépare n'est
 * pas la qualité de l'accord mais le **nombre d'hypothèses libres** qu'il faut
 * leur concéder.
 *
 * - **Galilée** — tous les corps tombent de la même façon. `t = √(2h/g)`.
 *   Zéro hypothèse libre : la hauteur et la pesanteur sont les données du
 *   problème, rien d'autre n'entre.
 * - **Aristote** — la vitesse de chute est proportionnelle au poids et
 *   inversement proportionnelle à la résistance du milieu. Une hypothèse
 *   libre : la vitesse qu'atteint une masse de référence dans ce milieu,
 *   qu'aucune théorie ne fixe et qu'il faut donc donner.
 *
 * L'observable qui les départage est immédiate : **lâchez deux masses
 * différentes**. Galilée dit qu'elles touchent le sol ensemble, Aristote dit
 * que la lourde arrive la première. Aucune éphéméride, aucun réseau de
 * partenaires, aucune campagne de cinq mois — un escabeau et un téléphone.
 *
 * ## Ce que l'intégrateur fait ici, et ce qu'il n'y fait pas
 *
 * `fallTrajectory` intègre pas à pas, et c'est le premier usage réel du noyau
 * d'intégration. Il faut cependant être honnête sur ce que ça prouve : sous
 * pesanteur constante, la solution est un polynôme du second degré que
 * velocity Verlet reproduit exactement. L'intégrateur n'est donc pas
 * *nécessaire* à cette expérience — il y est exercé, pas justifié. Ce qui le
 * rendra indispensable est l'orbite (§9), où la pesanteur varie avec la
 * distance et où aucune forme close élémentaire n'existe.
 *
 * La trajectoire d'Aristote, elle, ne passe pas par l'intégrateur du tout, et
 * pas par paresse : sa physique énonce une loi sur la **vitesse**, pas sur
 * l'accélération. Elle n'est pas du second ordre, et velocity Verlet n'a rien
 * à y intégrer. Le constater vaut mieux que de la forcer dans un moule qui ne
 * lui va pas.
 */

import { coreFr } from "../i18n/core-fr.js";
import { max, round, sqrt } from "./math.js";
import type { FreeHypotheses, ModelDescriptor } from "./models.js";
import { integrate, step } from "./integrator.js";
import type { Body, Field } from "./integrator.js";
import type { Quantity } from "./units.js";
import { dimensionless, quantity, valueIn, velocityChange } from "./units.js";

export type FallModel =
  | (ModelDescriptor<"galileo-fall"> & { readonly law: "uniform-acceleration" })
  | (ModelDescriptor<"aristotle-fall"> & { readonly law: "weight-proportional-speed" });

// ── Les corps célestes ───────────────────────────────────────────────────────

export interface CelestialBody {
  readonly id: string;
  readonly label: string;
  readonly surfaceGravity: Quantity<"acceleration">;
}

/**
 * Pesanteurs de surface, en m/s².
 *
 * **Référence externe**, jamais un arbitre : ce sont des valeurs publiées, que
 * ce projet n'a pas mesurées. Elles entrent dans les prédictions des deux
 * modèles à l'identique, donc elles ne peuvent en favoriser aucun.
 */
export const BODIES: readonly CelestialBody[] = [
  { id: "lune", label: coreFr.fall.moon, surfaceGravity: quantity(1.625, "m/s2") },
  { id: "mars", label: coreFr.fall.mars, surfaceGravity: quantity(3.7207, "m/s2") },
  { id: "venus", label: coreFr.fall.venus, surfaceGravity: quantity(8.87, "m/s2") },
  { id: "terre", label: coreFr.fall.earth, surfaceGravity: quantity(9.80665, "m/s2") },
  { id: "jupiter", label: coreFr.fall.jupiter, surfaceGravity: quantity(24.79, "m/s2") },
];

export function bodyNamed(id: string): CelestialBody {
  const found = BODIES.find((body) => body.id === id);
  if (found === undefined) {
    throw new Error(coreFr.fall.unknownBody(String(id)));
  }
  return found;
}

// ── Les deux modèles ─────────────────────────────────────────────────────────

export interface FallSetup {
  readonly height: Quantity<"length">;
  readonly mass: Quantity<"mass">;
  readonly gravity: Quantity<"acceleration">;
  /** L'hypothèse libre d'Aristote. Absente, il refuse de prédire. */
  readonly referenceSpeed?: Quantity<"velocity">;
}

/**
 * Un modèle a le droit de ne rien prédire. On ne renvoie jamais une durée
 * inventée pour combler le trou.
 */
export type FallPrediction =
  | { readonly kind: "duration"; readonly time: Quantity<"time"> }
  | { readonly kind: "none"; readonly reason: string };

export const GALILEO_FALL: FallModel = {
  id: "galileo-fall",
  law: "uniform-acceleration",
  label: coreFr.fall.galileoLabel,
  freeHypotheses: { kind: "fixed", count: dimensionless(0) } satisfies FreeHypotheses,
  assumptions: [
    coreFr.fall.equalAcceleration,
    coreFr.fall.vacuumAssumption,
  ],
};

export const ARISTOTLE_FALL: FallModel = {
  id: "aristotle-fall",
  law: "weight-proportional-speed",
  label: coreFr.fall.aristotleLabel,
  freeHypotheses: { kind: "fixed", count: dimensionless(1) } satisfies FreeHypotheses,
  assumptions: [
    coreFr.fall.speedProportionalToWeight,
    coreFr.fall.speedInverselyProportionalToResistance,
    coreFr.fall.resistanceIsFree,
  ],
};

/** Masse de référence à laquelle se rapporte la vitesse d'Aristote. */
const REFERENCE_MASS = quantity(1, "kg");
/** Pesanteur de référence, la même. */
const REFERENCE_GRAVITY = quantity(9.80665, "m/s2");

function fallSpeedOfAristotle(setup: FallSetup): Quantity<"velocity"> | undefined {
  const reference = setup.referenceSpeed;
  if (reference === undefined) return undefined;

  // v ∝ poids = m·g, rapportée au poids de référence.
  const weight = valueIn(setup.mass, "kg") * valueIn(setup.gravity, "m/s2");
  const referenceWeight =
    valueIn(REFERENCE_MASS, "kg") * valueIn(REFERENCE_GRAVITY, "m/s2");
  const speed = (valueIn(reference, "m/s") * weight) / referenceWeight;
  return speed > 0 ? quantity(speed, "m/s") : undefined;
}

/** Durée de chute depuis la hauteur donnée, selon le modèle. */
export function fallTime(model: FallModel, setup: FallSetup): FallPrediction {
  const height = valueIn(setup.height, "m");
  if (height <= 0) {
    return { kind: "none", reason: coreFr.fall.positiveHeightRequired };
  }

  if (model.law === "uniform-acceleration") {
    const g = valueIn(setup.gravity, "m/s2");
    if (g <= 0) {
      return { kind: "none", reason: coreFr.fall.positiveGravityRequired };
    }
    return { kind: "duration", time: quantity(sqrt((2 * height) / g), "s") };
  }

  const speed = fallSpeedOfAristotle(setup);
  if (speed === undefined) {
    return {
      kind: "none",
      reason:
        coreFr.fall.referenceSpeedRequired,
    };
  }
  return { kind: "duration", time: quantity(height / valueIn(speed, "m/s"), "s") };
}

/**
 * Vitesse au moment où le corps touche le sol. Absente quand le modèle ne
 * prédit rien — on ne renvoie jamais un zéro pour combler.
 *
 * Les deux modèles n'y arrivent pas par le même chemin, et la différence est
 * leur physique même. Galilée énonce une loi sur l'**accélération** : la
 * vitesse croît tout du long, et vaut `g·t` à l'arrivée — soit `√(2gh)`.
 * Aristote énonce une loi sur la **vitesse** : elle est atteinte d'emblée et ne
 * change plus, si bien que la vitesse d'impact est la vitesse de chute.
 */
export function impactSpeed(
  model: FallModel,
  setup: FallSetup,
): Quantity<"velocity"> | undefined {
  const prediction = fallTime(model, setup);
  if (prediction.kind !== "duration") return undefined;

  return model.law === "weight-proportional-speed"
    ? fallSpeedOfAristotle(setup)
    : velocityChange(setup.gravity, prediction.time);
}

/**
 * Hauteur au-dessus du sol à un instant donné. C'est ce que la vue 3D anime.
 *
 * **Fonction pure d'un instant explicite.** L'animation choisit *quand*
 * regarder ; elle n'entre jamais dans le calcul. Rien n'est retenu d'un appel
 * à l'autre, si bien que la trajectoire affichée ne dépend ni du rythme de la
 * machine, ni du nombre d'images qu'elle a réussi à rendre — deux visites au
 * même instant donnent la même hauteur, et c'est ce que CLAUDE.md §5 exige.
 *
 * Le corps posé reste posé : passé l'impact, la hauteur est zéro, pas une
 * valeur négative qui ferait traverser le sol. Avant le lâcher, elle vaut la
 * hauteur de départ — un instant négatif est hors de l'expérience, pas une
 * chute à l'envers.
 */
export function fallHeightAt(
  model: FallModel,
  setup: FallSetup,
  elapsed: Quantity<"time">,
): Quantity<"length"> | undefined {
  const total = fallTime(model, setup);
  if (total.kind !== "duration") return undefined;

  const seconds = valueIn(elapsed, "s");
  if (seconds <= 0) return setup.height;
  if (seconds >= valueIn(total.time, "s")) return quantity(0, "m");

  if (model.law === "weight-proportional-speed") {
    const speed = fallSpeedOfAristotle(setup);
    if (speed === undefined) return undefined;
    const height = valueIn(setup.height, "m") - valueIn(speed, "m/s") * seconds;
    return quantity(max(0, height), "m");
  }

  // Galilée passe par l'intégrateur, comme sa trajectoire. Sous pesanteur
  // constante velocity Verlet est exact quel que soit le pas : un seul
  // suffirait, et le résultat ne dépendrait pas du découpage. On en fait
  // plusieurs pour que ce soit la même mécanique qui produise l'animation et
  // le graphe, et non deux chemins qu'un jour rien ne réconcilierait.
  const field: Field = () => ({
    x: quantity(0, "m/s2"),
    y: quantity(-valueIn(setup.gravity, "m/s2"), "m/s2"),
  });

  const body: Body = {
    position: { x: quantity(0, "m"), y: setup.height },
    velocity: { x: quantity(0, "m/s"), y: quantity(0, "m/s") },
    mass: setup.mass,
  };

  const landed = integrate(
    body,
    field,
    quantity(seconds / ANIMATION_STEPS, "s"),
    dimensionless(ANIMATION_STEPS),
  );
  return quantity(max(0, valueIn(landed.position.y, "m")), "m");
}

/** Pas d'intégration pour une image. Le résultat n'en dépend pas — voir ci-dessus. */
const ANIMATION_STEPS = 32;

// ── La trajectoire ───────────────────────────────────────────────────────────

export interface FallPoint {
  readonly time: Quantity<"time">;
  readonly height: Quantity<"length">;
}

/**
 * La chute échantillonnée, du lâcher au sol.
 *
 * Galilée passe par l'intégrateur ; Aristote non, faute d'une loi sur
 * l'accélération. Voir l'en-tête du module.
 */
export function fallTrajectory(
  model: FallModel,
  setup: FallSetup,
  samples: Quantity<"dimensionless">,
): readonly FallPoint[] {
  const total = fallTime(model, setup);
  if (total.kind !== "duration") return [];

  const count = max(1, round(valueIn(samples, "1")));
  const seconds = valueIn(total.time, "s");
  const dt = quantity(seconds / count, "s");
  const height = valueIn(setup.height, "m");

  if (model.law === "weight-proportional-speed") {
    const speed = fallSpeedOfAristotle(setup);
    if (speed === undefined) return [];
    const metresPerSecond = valueIn(speed, "m/s");
    const points: FallPoint[] = [];
    for (let index = 0; index <= count; index += 1) {
      const t = (seconds * index) / count;
      points.push({
        time: quantity(t, "s"),
        height: quantity(max(0, height - metresPerSecond * t), "m"),
      });
    }
    return points;
  }

  const field: Field = () => ({
    x: quantity(0, "m/s2"),
    y: quantity(-valueIn(setup.gravity, "m/s2"), "m/s2"),
  });

  let body: Body = {
    position: { x: quantity(0, "m"), y: setup.height },
    velocity: { x: quantity(0, "m/s"), y: quantity(0, "m/s") },
    mass: setup.mass,
  };

  const points: FallPoint[] = [{ time: quantity(0, "s"), height: setup.height }];
  for (let index = 1; index <= count; index += 1) {
    body = step(body, field, dt);
    points.push({
      time: quantity((seconds * index) / count, "s"),
      height: body.position.y,
    });
  }
  return points;
}
